"use client";

import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";
import Modal from "../components/common/Modal";
import UserManagementForm from "../components/auth/UserManagement";
import api from "../services/api";

const UserManagement = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch both users and roles on mount
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/users");
      setUsers(response.data.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to load users data",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await api.get("/roles");
      // Use response.data directly since the backend returns an array
      setRoles(response.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to load roles data",
      });
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleAddUser = async (userData) => {
    try {
      await api.post("/users", userData);
      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "User added successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      setShowAddModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Error adding user:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to add user",
      });
    }
  };

  const handleEditUser = async (userId, updatedUserData) => {
    try {
      await api.patch(`/users/${userId}`, updatedUserData);
      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "User updated successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      fetchUsers(); // Refresh the list
      setShowEditModal(false); // Close modal
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Update failed",
      });
    }
  };

  const handleDeleteUser = async (userId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/users/${userId}`);
          Swal.fire({
            position: "top-end",
            icon: "success",
            title: "User deleted successfully",
            showConfirmButton: false,
            timer: 1500,
          });
          fetchUsers();
        } catch (error) {
          console.error("Error deleting user:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error.response?.data?.message || "Failed to delete user",
          });
        }
      }
    });
  };

  // Helper: get role name by role ObjectId from the roles state
  const getRoleName = (roleId) => {
    const role = roles.find((r) => r._id === roleId);
    return role ? role.name : "N/A";
  };

  const columns = [
    { field: "firstName", header: "First Name" },
    { field: "lastName", header: "Last Name" },
    { field: "email", header: "Email" },
    {
      field: "role",
      header: "Role",
      render: (user) => {
        // If role is populated as an object with a name, use that
        if (user.role?.name) {
          return user.role.name;
        }
        // Otherwise, assume it's an ObjectId and use dynamic lookup
        const roleId = user.role?._id || user.role;
        return getRoleName(roleId);
      },
    },
    {
      field: "actions",
      header: "Actions",
      render: (user) => (
        <div className="flex space-x-3">
          <button
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={() => {
              setSelectedUser(user);
              setShowEditModal(true);
            }}
          >
            Edit
          </button>
          <button
            className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            onClick={() => handleDeleteUser(user._id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const Table = ({ data, columns }) => (
    <div className="overflow-x-auto bg-white rounded-lg shadow-md">
      <table className="min-w-full table-auto">
        <thead>
          <tr className="bg-gray-100 border-b">
            {columns.map((column) => (
              <th
                key={column.field}
                className="px-6 py-3 text-sm font-semibold tracking-wider text-left text-gray-700 uppercase"
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row._id} className="hover:bg-gray-50">
              {columns.map((column) => (
                <td
                  key={column.field}
                  className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap"
                >
                  {column.render ? column.render(row) : row[column.field]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <h1 className="mb-6 text-3xl font-bold text-gray-800">User Management</h1>
      <button
        className="px-6 py-3 mb-6 text-white bg-indigo-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        onClick={() => setShowAddModal(true)}
      >
        Add User
      </button>

      {loading || loadingRoles ? (
        <div className="text-lg text-center text-gray-500">Loading data...</div>
      ) : (
        <Table data={users} columns={columns} />
      )}

      {showAddModal && (
        <Modal title="Add User" onClose={() => setShowAddModal(false)}>
          <UserManagementForm
            onSave={handleAddUser}
            onCancel={() => setShowAddModal(false)}
            roles={roles}
          />
        </Modal>
      )}

      {showEditModal && selectedUser && (
        <Modal title="Edit User" onClose={() => setShowEditModal(false)}>
          <UserManagementForm
            user={selectedUser}
            onSave={handleEditUser}
            onCancel={() => setShowEditModal(false)}
            roles={roles}
          />
        </Modal>
      )}
    </div>
  );
};

export default UserManagement;
